import { assertCalendarIcsBytes } from '@streamlain/calendar-ics-security';
import { format } from 'date-fns/format';

import { dataViewI18nT } from '../../../../core/i18n.js';
import {
  type CalendarOccurrence,
  parseCalendarIcs,
} from '../../calendar-domain.js';
import type { CalendarSingleView } from '../../calendar-view-manager.js';
import type { CalendarIcsSubscription } from '../../define.js';
import type {
  CalendarEvent,
  CalendarSubscriptionState,
} from '../calendar-view-types.js';
import {
  createCalendarIcsSubscriptionFromUrl,
  createSignedCalendarFeedUrl,
  downloadCalendarIcs,
  fetchIcsSubscriptionEvents,
  renderCalendarEventsIcs,
} from './calendar-ics-service.js';
import {
  calendarReconcileColumns,
  flushCalendarReconcileDocs,
  resolveCalendarReconcileContext,
} from './calendar-reconcile-service.js';

type SetCellJson = (
  rowId: string,
  propertyId: string | undefined,
  value: unknown
) => void;

export type CalendarIcsActionsContext = {
  addRow: () => string | undefined;
  baseEvents: () => readonly CalendarEvent[];
  closeFeedForm: () => void;
  events: () => readonly CalendarOccurrence<CalendarEvent>[];
  getSubscriptionEvents: () => Readonly<Record<string, CalendarEvent[]>>;
  getSubscriptionLoadKey: () => string;
  getSubscriptionStates: () => Readonly<
    Record<string, CalendarSubscriptionState>
  >;
  readonly: () => boolean;
  requestCalendarUpdate: () => void;
  queueIntrinsicContentHeightChange: () => void;
  setCalendarError: (message: string | undefined) => void;
  setCellJson: SetCellJson;
  setSubscriptionEvents: (
    events: Readonly<Record<string, CalendarEvent[]>>
  ) => void;
  setSubscriptionLoadKey: (key: string) => void;
  setSubscriptionStates: (
    states: Readonly<Record<string, CalendarSubscriptionState>>
  ) => void;
  view: CalendarSingleView;
};

function setSubscriptionState(
  context: CalendarIcsActionsContext,
  id: string,
  state: CalendarSubscriptionState
): void {
  context.setSubscriptionStates({
    ...context.getSubscriptionStates(),
    [id]: state,
  });
}

export function ensureCalendarSubscriptionEventsLoaded(
  context: CalendarIcsActionsContext,
  subscriptions = context.view.calendarIcsSubscriptions$.value
): void {
  const loadKey = JSON.stringify(
    subscriptions.map(subscription => ({
      enabled: subscription.enabled !== false,
      id: subscription.id,
      url: subscription.url,
    }))
  );
  if (loadKey === context.getSubscriptionLoadKey()) return;
  context.setSubscriptionLoadKey(loadKey);
  const enabledIds = new Set(
    subscriptions
      .filter(subscription => subscription.enabled !== false)
      .map(subscription => subscription.id)
  );
  context.setSubscriptionEvents(
    Object.fromEntries(
      Object.entries(context.getSubscriptionEvents()).filter(([id]) =>
        enabledIds.has(id)
      )
    )
  );
  for (const subscription of subscriptions) {
    if (subscription.enabled === false) continue;
    void refreshCalendarIcsSubscription(context, subscription);
  }
}

export async function refreshCalendarIcsSubscription(
  context: CalendarIcsActionsContext,
  subscription: CalendarIcsSubscription
): Promise<void> {
  setSubscriptionState(context, subscription.id, { loading: true });
  try {
    const events = await fetchIcsSubscriptionEvents(subscription);
    context.setSubscriptionEvents({
      ...context.getSubscriptionEvents(),
      [subscription.id]: events,
    });
    setSubscriptionState(context, subscription.id, {
      loading: false,
      refreshedAt: new Date().toISOString(),
    });
    context.setCalendarError(undefined);
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.feedFailed');
    context.setSubscriptionEvents({
      ...context.getSubscriptionEvents(),
      [subscription.id]: [],
    });
    setSubscriptionState(context, subscription.id, {
      error: message,
      loading: false,
    });
    context.setCalendarError(
      dataViewI18nT('calendar.error.feedLoadFailed', {
        message,
        name: subscription.name || subscription.url,
      })
    );
  }
}

export function downloadVisibleCalendarIcs(
  context: CalendarIcsActionsContext
): void {
  const visibleRows = new Set(
    context.events().map(event => event.source.rowId)
  );
  const events = context
    .baseEvents()
    .filter(event => visibleRows.has(event.rowId));
  try {
    downloadCalendarIcs(
      renderCalendarEventsIcs(events),
      `streamlain-calendar-${format(new Date(), 'yyyy-MM-dd')}.ics`
    );
    context.setCalendarError(undefined);
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.exportFailed')
    );
  }
}

export function downloadCalendarEventIcs(
  context: CalendarIcsActionsContext,
  event: CalendarEvent
): void {
  try {
    downloadCalendarIcs(
      renderCalendarEventsIcs([event]),
      `${event.title || 'event'}.ics`
    );
    context.setCalendarError(undefined);
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.exportFailed')
    );
  }
}

export async function copyCalendarSubscriptionUrl(
  context: CalendarIcsActionsContext
): Promise<void> {
  if (context.readonly()) return;
  const reconcileContext = resolveCalendarReconcileContext(
    context.view.manager.dataSource
  );
  const columns = calendarReconcileColumns(context.view);
  if (!reconcileContext || !columns) return;
  try {
    const absoluteUrl = await createSignedCalendarFeedUrl({
      columns,
      context: reconcileContext,
      flushDocs: flushCalendarReconcileDocs,
      origin: window.location.origin,
      viewId: context.view.id,
    });
    await navigator.clipboard?.writeText(absoluteUrl);
    context.setCalendarError(undefined);
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.feedUrlCreateFailed')
    );
  }
}

export function addCalendarIcsSubscriptionFromUrl(
  context: CalendarIcsActionsContext,
  url: string
): void {
  if (context.readonly()) return;
  try {
    const subscription = createCalendarIcsSubscriptionFromUrl(url);
    context.view.setCalendarIcsSubscriptions([
      ...context.view.calendarIcsSubscriptions$.value,
      subscription,
    ]);
    context.setSubscriptionLoadKey('');
    context.closeFeedForm();
    context.setCalendarError(undefined);
    context.requestCalendarUpdate();
    context.queueIntrinsicContentHeightChange();
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.feedInvalid')
    );
  }
}

export function removeCalendarIcsSubscription(
  context: CalendarIcsActionsContext,
  subscriptionId: string
): void {
  if (context.readonly()) return;
  context.view.setCalendarIcsSubscriptions(
    context.view.calendarIcsSubscriptions$.value.filter(
      subscription => subscription.id !== subscriptionId
    )
  );
  const { [subscriptionId]: _removedEvents, ...events } =
    context.getSubscriptionEvents();
  const { [subscriptionId]: _removedState, ...states } =
    context.getSubscriptionStates();
  context.setSubscriptionEvents(events);
  context.setSubscriptionStates(states);
  context.setSubscriptionLoadKey('');
  context.requestCalendarUpdate();
}

export function setCalendarIcsSubscriptionEnabled(
  context: CalendarIcsActionsContext,
  subscriptionId: string,
  enabled: boolean
): void {
  if (context.readonly()) return;
  context.view.setCalendarIcsSubscriptions(
    context.view.calendarIcsSubscriptions$.value.map(subscription =>
      subscription.id === subscriptionId
        ? { ...subscription, enabled }
        : subscription
    )
  );
  context.setSubscriptionLoadKey('');
  context.requestCalendarUpdate();
}

export async function importCalendarIcsFile(
  context: CalendarIcsActionsContext,
  change: Event
): Promise<void> {
  const input = change.currentTarget as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file || context.readonly()) return;
  try {
    assertCalendarIcsBytes(file.size);
    importCalendarIcsText(context, await file.text());
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.importFailed')
    );
  }
}

export function importCalendarIcsText(
  context: CalendarIcsActionsContext,
  text: string
): void {
  const view = context.view;
  view.ensureCalendarColumns();
  let events: ReturnType<typeof parseCalendarIcs>;
  try {
    events = parseCalendarIcs(text);
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.importFailed')
    );
    return;
  }
  for (const event of events) {
    const rowId = context.addRow();
    if (!rowId) continue;
    context.setCellJson(
      rowId,
      view.mainProperties$.value.titleColumn,
      event.title
    );
    context.setCellJson(
      rowId,
      view.startDateCol$.value,
      event.startAt.getTime()
    );
    context.setCellJson(rowId, view.endDateCol$.value, event.endAt.getTime());
    context.setCellJson(rowId, view.allDayCol$.value, event.allDay);
    context.setCellJson(rowId, view.timezoneCol$.value, event.timezone);
    context.setCellJson(rowId, view.calendarIdCol$.value, event.calendarId);
    context.setCellJson(
      rowId,
      view.locationPhysicalCol$.value,
      event.locationPhysical
    );
    context.setCellJson(
      rowId,
      view.locationVirtualCol$.value,
      event.locationVirtual
    );
    context.setCellJson(rowId, view.recurrenceCol$.value, event.recurrence);
    context.setCellJson(
      rowId,
      view.recurrenceExdateCol$.value,
      event.recurrenceExdate
    );
    context.setCellJson(rowId, view.recurrenceIdCol$.value, event.recurrenceId);
    context.setCellJson(rowId, view.attendeesCol$.value, event.attendees);
    context.setCellJson(rowId, view.descriptionCol$.value, event.description);
    context.setCellJson(rowId, view.statusCol$.value, event.status);
  }
  context.setCalendarError(undefined);
}
